import {
  getFinishReadinessLabel,
  getLineItemMaterialOptionLabel,
  inferMaterialClassification,
} from "./materialFlow";

// Format currency values
export function formatCurrency(amount) {
  if (amount == null) return "$0.00"; // Handle null/undefined
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Common decimal-inch → fraction labels for material thickness (½″, ¾″, …).
const THICKNESS_FRACTIONS = {
  0.125: "1/8",
  0.25: "1/4",
  0.375: "3/8",
  0.5: "1/2",
  0.625: "5/8",
  0.75: "3/4",
  0.875: "7/8",
};

/**
 * Format a thickness in inches as a fraction where clean (0.5 → 1/2″,
 * 0.75 → 3/4″), otherwise a trimmed decimal (1.25 → 1.25″). Returns "" for
 * null/blank/non-numeric input.
 */
export function formatThicknessInches(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  const whole = Math.trunc(num);
  const frac = Math.round((num - whole) * 1000) / 1000;
  const fracLabel = THICKNESS_FRACTIONS[frac];
  if (fracLabel) {
    return whole > 0 ? `${whole} ${fracLabel}″` : `${fracLabel}″`;
  }
  return `${Number(num.toFixed(3))}″`;
}

// Format percentages
export function formatPercentage(value) {
  if (value == null) return "0%"; // Handle null/undefined
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100); // Assuming input is 42 for 42%
}

/**
 * Automatically generates a description for a line item based on its properties
 * @param {Object} lineItem - The line item object
 * @param {Array} products - Array of available products
 * @param {Array} materials - Array of available materials
 * @returns {String} - Generated description
 */
export function autoGenerateDescription(lineItem, products, materials = []) {
  if (!lineItem) return "";

  // Get product - material is now embedded in product
  const product = products.find((p) => p.id === lineItem.product_id);

  if (!product) {
    return ""; // Return empty string if product not selected
  }

  // Get material from product's embedded material (preferred) or fallback to materials array
  const material =
    product.material || materials.find((m) => m.id === lineItem.material_id);

  // Can proceed without material - just won't include material type in description
  if (!material) {
    // Continue with product-only description
  }

  // Format dimensions with inches symbol (″)
  const formattedLength = lineItem.length ? `${lineItem.length}″` : "";
  const formattedWidth = lineItem.width ? `${lineItem.width}″` : "";
  const formattedHeight = lineItem.height ? `${lineItem.height}″` : "";

  // Build dimension string - only include dimensions that have values
  let dimensions = "";
  if (formattedWidth && formattedHeight && formattedLength) {
    dimensions = `${formattedWidth}W × ${formattedHeight}H × ${formattedLength}L`;
  } else if (formattedWidth && formattedHeight) {
    dimensions = `${formattedWidth}W × ${formattedHeight}H`;
  } else if (formattedWidth && formattedLength) {
    dimensions = `${formattedWidth}W × ${formattedLength}L`;
  } else if (formattedHeight && formattedLength) {
    dimensions = `${formattedHeight}H × ${formattedLength}L`;
  } else if (formattedLength) {
    dimensions = `${formattedLength}L`;
  } else if (formattedWidth) {
    dimensions = `${formattedWidth}W`;
  } else if (formattedHeight) {
    dimensions = `${formattedHeight}H`;
  }

  // Prefer an explicit estimator classification, then fall back to the
  // product-linked material for quote-friendly shorthand.
  let materialType = "";
  const configuredOptionLabel = getLineItemMaterialOptionLabel({
    lineItem,
    product,
  });
  if (configuredOptionLabel) {
    materialType = configuredOptionLabel;
  } else {
    const inferredMaterialType =
      lineItem.material_classification ||
      inferMaterialClassification(material?.name || "");
    if (inferredMaterialType && inferredMaterialType !== "Other") {
      materialType = inferredMaterialType;
    }
  }

  const finishReadiness = getFinishReadinessLabel(lineItem.finish_readiness);

  // Generate the full description - now using the extracted material type instead of full material name
  if (dimensions) {
    if (materialType) {
      return finishReadiness
        ? `${dimensions} ${materialType} ${product.name} · ${finishReadiness}`
        : `${dimensions} ${materialType} ${product.name}`;
    } else {
      return finishReadiness
        ? `${dimensions} ${product.name} · ${finishReadiness}`
        : `${dimensions} ${product.name}`;
    }
  } else {
    if (materialType) {
      return finishReadiness
        ? `${materialType} ${product.name} · ${finishReadiness}`
        : `${materialType} ${product.name}`;
    } else {
      return finishReadiness
        ? `${product.name} · ${finishReadiness}`
        : product.name;
    }
  }
}
